import { useMemo } from 'react'

import { Formik, Form } from 'formik'
import { string, object, array, SchemaOf, number } from 'yup'
import moment from 'moment'

import { Alert, Collapse } from '@mui/material'

import {
  FormTextField,
  FormDatePicker,
  maxLength,
  noWhitespaces,
  requiredField,
  invalidFieldWithName,
  FormSelect,
} from '@collaborate-health/forms'
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Stack } from '@collaborate-health/uikit'
import { useAllCertificateTypes } from '@tabeeb/modules/certificates/hooks'
import {
  CertificateTypeVisibilityScope,
  type CreateQualificationRequest,
} from '@tabeeb/contracts/microservices/swagger-certificate-service'
import { CertificateTypeField } from '@tabeeb/modules/certificates/components/Fields'
import { useCurrentCorporation } from '@tabeeb/modules/admin/corporations/hooks'

import { QualificationVisibilityScope, QualificationVisibilityScopeDisplayName } from '../../enums'

import { AlertList } from './AddQualificationDialog.styles'

const schema: SchemaOf<CreateQualificationRequest> = object().shape({
  Name: string().strict().trim(noWhitespaces).required(requiredField).max(100, maxLength(100)),
  VisibilityScope: number().required(),
  CertificateTypeIds: array()
    .of(number().strict().required())
    .min(1, 'At least one type must be selected')
    .required(requiredField),
  ExpirationDate: string()
    .test('min-date', invalidFieldWithName('Expiration date'), (value) => {
      if (!value) {
        return true
      }

      return moment(value).isAfter(moment.utc().startOf('day'))
    })
    .nullable(),
})

export interface AddQualificationDialogProps {
  open: boolean
  existing?: boolean
  initial?: CreateQualificationRequest
  title?: string
  onClose: () => void
  onExited?: () => void
  onSubmit: (values: CreateQualificationRequest) => void
}

const AddQualificationDialog: React.FC<AddQualificationDialogProps> = ({
  open,
  existing = false,
  initial,
  title = 'Create qualification',
  onClose,
  onExited,
  onSubmit,
}) => {
  const initialValues = useMemo<CreateQualificationRequest>(
    () => ({
      Name: initial?.Name ?? '',
      VisibilityScope: initial?.VisibilityScope ?? QualificationVisibilityScope.Tenant,
      CertificateTypeIds: initial?.CertificateTypeIds ?? [],
      ExpirationDate: initial?.ExpirationDate ?? null,
    }),
    [initial?.CertificateTypeIds, initial?.ExpirationDate, initial?.Name, initial?.VisibilityScope]
  )

  const { canManageCorporationCertificates } = useCurrentCorporation()
  const { loading: certificateTypesLoading, certificateTypes } = useAllCertificateTypes({ enabled: open })

  const availableVisibilityScopes = useMemo<QualificationVisibilityScope[]>(() => {
    const scopes = [QualificationVisibilityScope.Tenant]

    if (canManageCorporationCertificates) {
      scopes.push(QualificationVisibilityScope.Corporation)
    }

    return scopes
  }, [canManageCorporationCertificates])

  return (
    <Dialog open={open} onClose={onClose} TransitionProps={{ onExited }} fullWidth maxWidth='sm'>
      <DialogTitle>{title}</DialogTitle>
      <Formik initialValues={initialValues} validationSchema={schema} validateOnMount onSubmit={onSubmit}>
        {({ isValid, isSubmitting, dirty, values }) => {
          const selectedCertificateTypesIds = new Set(values.CertificateTypeIds)

          const selectedCertificateTypesWithTenantScope =
            certificateTypes.filter(
              (ct) =>
                selectedCertificateTypesIds.has(ct.Id) && ct.VisibilityScope === CertificateTypeVisibilityScope.Tenant
            ) ?? []

          return (
            <Form style={{ display: 'contents' }} autoComplete='off'>
              <DialogContent>
                <Stack spacing={2}>
                  <FormTextField name='Name' label='Name' required />
                  <CertificateTypeField
                    name='CertificateTypeIds'
                    multiple
                    disableCloseOnSelect
                    types={certificateTypes}
                    loading={certificateTypesLoading}
                    limitTags={10}
                  />
                  <FormDatePicker
                    fullWidth
                    name='ExpirationDate'
                    label='Valid till (optional)'
                    disablePast
                    format='D MMMM YYYY'
                  />
                  <Collapse in={!existing && availableVisibilityScopes.length > 1}>
                    <FormSelect
                      name='VisibilityScope'
                      label='Visibility scope'
                      required
                      options={availableVisibilityScopes.map((value) => ({
                        name: QualificationVisibilityScopeDisplayName[value],
                        value,
                      }))}
                    />
                  </Collapse>
                  <Collapse
                    in={
                      values.VisibilityScope === QualificationVisibilityScope.Corporation &&
                      selectedCertificateTypesWithTenantScope.length > 0
                    }
                    unmountOnExit
                  >
                    <Alert severity='error'>
                      Corporation-level qualifications cannot include tenant-level certificate types. Please remove the
                      following certificate types or change the visibility scope:
                      <AlertList>
                        {selectedCertificateTypesWithTenantScope.map((ct) => (
                          <li key={ct.Id}>{ct.Name}</li>
                        ))}
                      </AlertList>
                    </Alert>
                  </Collapse>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button color='primary' onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  variant='contained'
                  color='primary'
                  type='submit'
                  loading={isSubmitting}
                  disabled={
                    !(isValid && dirty) ||
                    (values.VisibilityScope === QualificationVisibilityScope.Corporation &&
                      selectedCertificateTypesWithTenantScope.length > 0)
                  }
                >
                  Save
                </Button>
              </DialogActions>
            </Form>
          )
        }}
      </Formik>
    </Dialog>
  )
}

export default AddQualificationDialog

